import React from 'react';
import { Typography, Tooltip } from '@mui/material';
import { EditRounded, DeleteRounded, GroupWorkRounded } from '@mui/icons-material';
import {
	CardContainer,
	ColorStripe,
	CardBody,
	CardTopRow,
	EntityAvatar,
	AvatarImage,
	ChildCountBadge,
	CardFooter,
	ActionButton,
} from './styles';

const FALLBACK_COLOR = '#1565C0';

const cardColors = (hex: string): { stripe: string; accent: string; bg: string } => {
	const value = /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : FALLBACK_COLOR;
	const red = parseInt(value.slice(1, 3), 16);
	const green = parseInt(value.slice(3, 5), 16);
	const blue = parseInt(value.slice(5, 7), 16);
	return { stripe: value, accent: value, bg: `rgba(${red}, ${green}, ${blue}, 0.12)` };
};

interface GestionCardProps {
	name: string;
	description?: string;
	childCount?: number;
	childLabel?: string;
	color: string;
	imageUrl?: string | null;
	onSelect?: () => void;
	onEdit: () => void;
	onDelete: () => void;
}

const getInitials = (name: string): string =>
	name
		.split(' ')
		.filter((w) => w.length > 1)
		.slice(0, 2)
		.map((w) => w[0])
		.join('')
		.toUpperCase()
		.padEnd(2, name[1]?.toUpperCase() ?? 'X');

const GestionCard: React.FC<GestionCardProps> = ({
	name,
	description,
	childCount,
	childLabel,
	color,
	imageUrl,
	onSelect,
	onEdit,
	onDelete,
}) => {
	const colors = cardColors(color);
	const isNavigable = !!onSelect;

	const handleEdit = (e: React.MouseEvent) => {
		e.stopPropagation();
		onEdit();
	};

	const handleDelete = (e: React.MouseEvent) => {
		e.stopPropagation();
		onDelete();
	};

	return (
		<CardContainer navigable={isNavigable} onClick={isNavigable ? onSelect : undefined}>
			<ColorStripe stripecolor={colors.stripe} />
			<CardBody>
				<CardTopRow>
					<EntityAvatar bgcolor={colors.bg} accentcolor={colors.accent}>
						{imageUrl ? <AvatarImage src={imageUrl} alt='' /> : getInitials(name)}
					</EntityAvatar>
					{childCount !== undefined && childLabel && (
						<ChildCountBadge accentcolor={colors.accent}>
							<GroupWorkRounded style={{ fontSize: '0.85rem' }} />
							{childCount} {childCount === 1 ? childLabel : `${childLabel}s`}
						</ChildCountBadge>
					)}
				</CardTopRow>

				<Typography variant='subtitle1' fontWeight={700} noWrap title={name}>
					{name}
				</Typography>

				{description && (
					<Typography variant='body2' color='text.secondary' noWrap title={description}>
						{description}
					</Typography>
				)}

				<CardFooter>
					<Tooltip title='Editar' arrow>
						<ActionButton size='small' onClick={handleEdit}>
							<EditRounded fontSize='small' />
						</ActionButton>
					</Tooltip>
					<Tooltip title='Eliminar' arrow>
						<ActionButton size='small' onClick={handleDelete} color='error'>
							<DeleteRounded fontSize='small' />
						</ActionButton>
					</Tooltip>
				</CardFooter>
			</CardBody>
		</CardContainer>
	);
};

export default GestionCard;
